import React from "react";
import ReactDOM from "react-dom";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import { ReactQueryConfigProvider } from "react-query";

import { ROLE_ADMIN } from "utils/constants";
import {
  useValidateLogin,
  useValidateAccessPermission,
  getIsUserLoggedIn,
  getUser,
  initChatBot,
} from "utils/auth";

import { AppContextProvider } from "contexts/AppContext/AppContext";
import LocaleProvider from "contexts/LocaleProvider/LocaleProvider";

import LoginLayout from "layouts/LoginLayout/LoginLayout";
import MainMenuLayout from "layouts/MainMenuLayout/MainMenuLayout";
import SubMenuLayout from "layouts/SubMenuLayout/SubMenuLayout";

import Base from "pages/Base/Base";
import Login from "pages/Login/Login";
import ForgotPassword from "pages/ForgotPassword/ForgotPassword";
import SignUp from "pages/SignUp/SignUp";
import NotFound404 from "pages/404/NotFound404";
import ComingSoon from "pages/ComingSoon/ComingSoon";

import Policies from "pages/Policies/Policies";
import Profile from "pages/Profile/Profile";
import Advisees from "pages/Advisees/Advisees";
import Advisee from "pages/Advisee/Advisee";
import AdviseeWizard from "pages/AdviseeWizard/AdviseeWizard";
import ResetPassword from "pages/ResetPassword/ResetPassword";
import Settings from "pages/Settings/Settings";
import FinportGoals from "pages/FinportGoals/FinportGoals";
import Users from "pages/Users/Users";
import User from "pages/User/User";
import EmergencyContact from "pages/EmergencyContact/EmergencyContact";
import AdminEmergencyContactTemplate from "pages/AdminEmergencyContactTemplate/AdminEmergencyContactTemplate";
import Dashboard from "pages/Dashboard/Dashboard";

import LeadFrom from "pages/LeadForm/LeadForm";
import LeadDashboard from "pages/LeadDashboard/LeadDashboard";

import Billing from "pages/Billing/Billing";
import BillingSubscription from "pages/Billing/BillingSubscription";
import BillingManagement from "pages/Billing/BillingManagement";
import BillingSuccessPage from "pages/Billing/BillingSuccessPage";
import BillingCancelPage from "pages/Billing/BillingCancelPage";

import {
  build404Uri,
  buildComingSoonUri,
  buildBaseUri,
  buildLoginUri,
  buildProfileUri,
  buildResetPasswordUri,
  buildGlobalSettingUri,
  buildFinportGoalsUri,
  buildSettingUri,
  buildPoliciesUri,
  buildAdviseesUri,
  buildAdviseeUri,
  buildAdviseeWizardUri,
  buildUsersUri,
  buildUserUri,
  buildUserNewUri,
  buildForgotPasswordUri,
  buildSignUpUri,
  buildLeadFormUri,
  buildLeadDashboardUri,
  buildEmergencyContactUri,
  buildManageEmergencyContactTemplateUri,
  buildDashboardUri,
  buildBillingUri,
  buildBillingSubscriptionSpecialUri,
  buildBillingSubscriptionUri,
  buildBillingManagementUri,
  buildBillingSuccessUri,
  buildBillingCancelUri,
  buildInvoicePDFViewerUri,
} from "utils/routes";

import * as serviceWorker from "./serviceWorker";
import "./index.css";
import TNCMOdal from "./components/TNCModal/TNCModal";
import BillingSubscriptionSpecial from "pages/Billing/BillingSubscriptionSpecial";
import InvoicePDFViewer from "pages/Invoice/InvoicePDFViewer";
import SocketConnection from "apis/socket/socket";
import Cookies from "universal-cookie";

var cookie = new Cookies();
const getLocale = cookie.get("localization");

const queryConfig = {
  retry: false,
  refetchOnWindowFocus: false,
};

const MainMenuLayoutRoute = ({ children, permittedRole, ...options }) => {
  useValidateAccessPermission(permittedRole, options);
  useValidateLogin(options);
  return (
    <Route {...options}>
      <MainMenuLayout>{children}</MainMenuLayout>
    </Route>
  );
};

const SubMenuLayoutRoute = ({ children, ...options }) => {
  useValidateLogin(options);
  return (
    <Route {...options}>
      <SubMenuLayout>{children}</SubMenuLayout>
    </Route>
  );
};

const LoginLayoutRoute = ({ children, ...options }) => (
  <Route {...options}>
    <LoginLayout>{children}</LoginLayout>
  </Route>
);

if (process.env.NODE_ENV === "production") {
  console.log = function no_console() {};
}

const setupConnection = async () => {
  const socket = await SocketConnection.initSocket();

  socket.on("connect", () => {
    console.log("Socket Connected");
    // message.destroy();
    // message.success("Connection restored");
  });

  socket.on("disconnect", () => {
    console.log("Socket disconnect");
    // message.destroy();
    // message.error("Network error, try again later");
  });
};

const isUserLogedIn = getIsUserLoggedIn();
if (isUserLogedIn) {
  const userInfo = getUser();
  if (userInfo) {
    initChatBot(userInfo);

    // GPT-Solutions init chatbot
    setupConnection();
  }
}

ReactDOM.render(
  <ReactQueryConfigProvider config={queryConfig}>
    <div className={getLocale === "zh-CN" ? "zh-cn-body" : "en-us-body"}>
      <Router>
        <LocaleProvider>
          <AppContextProvider>
            <TNCMOdal />
            <Switch>
              <Route exact path={build404Uri()}>
                <NotFound404 />
              </Route>
              <Route exact path={buildComingSoonUri()}>
                <ComingSoon />
              </Route>

              <Route exact path={buildBaseUri()}>
                <Base />
              </Route>

              <LoginLayoutRoute exact path={buildForgotPasswordUri()}>
                <ForgotPassword />
              </LoginLayoutRoute>

              <LoginLayoutRoute exact path={buildLoginUri()}>
                {/* <ForgotPassword /> */}
                <Login />
              </LoginLayoutRoute>

              <LoginLayoutRoute exact path={buildSignUpUri()}>
                <SignUp />
              </LoginLayoutRoute>

              <LoginLayoutRoute exact path={buildLeadFormUri()}>
                <LeadFrom />
              </LoginLayoutRoute>

              <MainMenuLayoutRoute exact path={buildProfileUri()}>
                <Profile title={buildProfileUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute exact path={buildResetPasswordUri()}>
                <ResetPassword title={buildResetPasswordUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute
                exact
                path={buildGlobalSettingUri()}
                permittedRole={ROLE_ADMIN}
              >
                <Settings title={buildGlobalSettingUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute
                exact
                path={buildSettingUri()}
                // permittedRole={ROLE_ADMIN}
              >
                <Settings title={buildSettingUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute
                exact
                path={buildFinportGoalsUri()}
                // permittedRole={ROLE_ADMIN}
              >
                <FinportGoals title={buildFinportGoalsUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute exact path={buildLeadDashboardUri()}>
                <LeadDashboard title={buildLeadDashboardUri()} />
              </MainMenuLayoutRoute>

              {/* --------------------------- Users --------------------------- */}

              <MainMenuLayoutRoute
                exact
                path={buildUsersUri()}
                permittedRole={ROLE_ADMIN}
              >
                <Users title={buildUsersUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute
                exact
                path={buildUserNewUri()}
                permittedRole={ROLE_ADMIN}
              >
                <User title={buildUserNewUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute
                exact
                path={buildUserUri()}
                permittedRole={ROLE_ADMIN}
              >
                <User title={buildUserUri()} />
              </MainMenuLayoutRoute>

              {/* --------------------------- Advisees --------------------------- */}
              <MainMenuLayoutRoute exact path={buildAdviseesUri()}>
                <Advisees title={buildAdviseesUri()} />
              </MainMenuLayoutRoute>

              <Route path={buildAdviseeWizardUri()}>
                <AdviseeWizard />
              </Route>

              <SubMenuLayoutRoute path={buildAdviseeUri()}>
                <Advisee />
              </SubMenuLayoutRoute>

              {/* --------------------------- Policies --------------------------- */}
              <MainMenuLayoutRoute
                path={buildPoliciesUri()}
                // permittedRole={ROLE_ADMIN}
              >
                <Policies title={buildPoliciesUri()} />
              </MainMenuLayoutRoute>

              {/* --------------------------- Emergency Contact --------------------------- */}
              <MainMenuLayoutRoute
                path={buildEmergencyContactUri()}
                // permittedRole={ROLE_ADMIN}
              >
                <EmergencyContact title={buildEmergencyContactUri()} />
              </MainMenuLayoutRoute>

              {/* ---------------------------Admin Manage Emergency Contact Template --------------------------- */}
              <MainMenuLayoutRoute
                path={buildManageEmergencyContactTemplateUri()}
                permittedRole={ROLE_ADMIN}
              >
                <AdminEmergencyContactTemplate
                  title={buildEmergencyContactUri()}
                />
              </MainMenuLayoutRoute>

              {/* --------------------------- Dashboard --------------------------- */}
              <MainMenuLayoutRoute
                path={buildDashboardUri()}
                // permittedRole={ROLE_ADMIN}
              >
                <Dashboard title={buildDashboardUri()} />
              </MainMenuLayoutRoute>

              {/* --------------------------- Billing --------------------------- */}
              <MainMenuLayoutRoute exact path={buildBillingUri()}>
                <Billing title={buildBillingUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute exact path={buildBillingSubscriptionUri()}>
                <BillingSubscription title={buildBillingSubscriptionUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute
                exact
                path={buildBillingSubscriptionSpecialUri()}
              >
                <BillingSubscriptionSpecial
                  title={buildBillingSubscriptionSpecialUri()}
                />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute exact path={buildBillingManagementUri()}>
                <BillingManagement title={buildBillingManagementUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute exact path={buildBillingSuccessUri()}>
                <BillingSuccessPage title={buildBillingSuccessUri()} />
              </MainMenuLayoutRoute>

              <MainMenuLayoutRoute exact path={buildBillingCancelUri()}>
                <BillingCancelPage title={buildBillingCancelUri()} />
              </MainMenuLayoutRoute>

              {/* --------------------------- Invoice --------------------------- */}
              <MainMenuLayoutRoute exact path={buildInvoicePDFViewerUri()}>
                <InvoicePDFViewer title={buildInvoicePDFViewerUri()} />
              </MainMenuLayoutRoute>

              <Redirect from="*" to={build404Uri()} />
            </Switch>
          </AppContextProvider>
        </LocaleProvider>
      </Router>
    </div>
  </ReactQueryConfigProvider>,
  document.getElementById("root")
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
